/**
 * Real-time user dashboard sync — ONE listener per user on users/{uid}.
 * Wallet + subscription + profile fields (games come from /games via gamesService).
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

function roundMoney(amount) {
  return Math.round(Number(amount || 0) * 100) / 100;
}

/** Map Firestore user doc → hydration sections (priority + secondary). */
export function mapUserDocToRealtimeSections(uid, data) {
  if (!data || typeof data !== 'object') {
    return {
      priority: { wallet: null, subscription: null },
      secondary: { profile: null },
    };
  }

  const balance = roundMoney(data.walletBalance);
  const now = Date.now();

  const priority = {
    wallet: {
      balance,
      lastUpdated: now,
    },
    subscription: {
      tier: data.subscriptionTier ?? null,
      status: data.subscriptionStatus ?? null,
      customerId: data.stripeCustomerId ?? null,
      subscriptionId: data.stripeSubscriptionId ?? null,
      lastUpdated: now,
    },
  };

  const profile = {
    id: uid,
    displayName: data.displayName,
    displayNameLower: data.displayNameLower,
    usernameLower: data.usernameLower,
    favoriteSport: data.favoriteSport,
    avatarUrl: data.avatarUrl,
    walletBalance: balance,
    subscriptionTier: data.subscriptionTier ?? null,
    subscriptionStatus: data.subscriptionStatus ?? null,
    trialEndDate: data.trialEndDate ?? null,
    subscriptionCurrentPeriodEnd: data.subscriptionCurrentPeriodEnd ?? null,
    subscriptionEndDate: data.subscriptionEndDate ?? null,
    riskTally: data.riskTally ?? 0,
    accountLockEndsAt: data.accountLockEndsAt ?? null,
  };

  return {
    priority,
    secondary: { profile },
  };
}

/**
 * Single real-time listener for wallet, subscription, and profile.
 * @param {string} uid
 * @param {(sections: ReturnType<typeof mapUserDocToRealtimeSections>, meta: { initial: boolean }) => void} onUpdate
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeUserDashboard(uid, onUpdate, onError) {
  if (!uid) {
    return () => {};
  }

  let initial = true;
  const ref = doc(db, 'users', uid);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(
          { priority: { wallet: null, subscription: null }, secondary: { profile: null } },
          { initial }
        );
        initial = false;
        return;
      }
      const sections = mapUserDocToRealtimeSections(snap.id, snap.data());
      onUpdate(sections, { initial });
      initial = false;
    },
    (err) => {
      console.warn('subscribeUserDashboard:', err);
      onError?.(err);
    }
  );
}
